/**
 * What a picker tells the shared choice field about its own list.
 *
 * @remarks
 * This is the INPUT to `ChoiceField`, and it is deliberately not
 * `DependsOnState`. `DependsOnState` is what `resolveDependsOn` produces out of
 * the form, which is only one of the reasons a field may have nothing to offer:
 * a picker that fetches can also be fetching, or have failed, while its
 * `dependsOn` is perfectly ready. A field driven by the resolver alone could
 * never express those, so the two stay separate and a picker maps one onto the
 * other.
 *
 * @packageDocumentation
 */

/**
 * The state of the list behind one choice field.
 *
 * @remarks
 * What each member means, and what the field does with it:
 *
 * - `'awaiting'` for a field still waiting on another field, naming the ones
 *   that are empty. Nothing is selectable.
 * - `'fetching'` for a request in flight. Nothing is selectable, and a list
 *   that was already there STAYS there rather than being emptied.
 * - `'failed'` for a request that came back wrong. Whether the filler can still
 *   work is `severity`, not this member.
 * - `'ready'` for a list that is current, with an optional `notice` about the
 *   list itself, such as there being more than what is shown.
 */
import type { DependsOnState } from './depends-on';

export type ChoiceState =
  | { kind: 'awaiting'; missing: readonly string[] }
  | { kind: 'fetching' }
  | {
      /** Goes to the Alert's `description`, visible right away. */
      description?: string;
      kind: 'failed';
      /** Goes to the Alert's `title`. */
      message: string;
      retry?: () => void;
      severity: 'fatal' | 'warning';
    }
  | { kind: 'ready'; notice?: string | string[] };

/**
 * Maps what the form resolved onto what the field shows.
 *
 * @remarks
 * For a picker whose list comes straight out of the form, this is the whole
 * story: there is nothing to fetch, so it is never `'fetching'`, and the only
 * way it fails is the template being wrong, which is always fatal because a
 * template does not repair itself.
 *
 * A picker that fetches builds its own `ChoiceState` instead, and uses this
 * only for the part its `dependsOn` decides.
 *
 * @param state - What `resolveDependsOn` returned.
 * @param notice - What to say about the list itself when it is ready.
 * @returns The state for `ChoiceField`.
 */
export function choiceStateOf(
  state: DependsOnState,
  notice?: string | string[],
): ChoiceState {
  switch (state.kind) {
    case 'author-error':
      return { kind: 'failed', message: state.message, severity: 'fatal' };
    case 'awaiting':
      return { kind: 'awaiting', missing: state.missing };
    case 'ready':
      return notice === undefined
        ? { kind: 'ready' }
        : { kind: 'ready', notice };
    default: {
      const exhaustive: never = state;
      throw new Error(`Unhandled dependsOn state: ${String(exhaustive)}`);
    }
  }
}

/**
 * A stable string identifying the question a `dependsOn` answer belongs to
 *.
 *
 * @remarks
 * Content, not object identity: RJSF rebuilds `values` every render, and a
 * same-content rerender must not read as a new question.
 *
 * @param state - What `resolveDependsOn` returned.
 * @returns A key for `ChoiceField`'s `sourceKey`, or `undefined` when there is
 * no answer to key on yet.
 */
export function sourceKeyOf(state: DependsOnState): string | undefined {
  return state.kind === 'ready' ? JSON.stringify(state.values) : undefined;
}
